import { useState, useEffect } from 'react';
import {
    Button,
    useNotify,
    useRefresh,
    usePermissions,
    useGetIdentity,
    useDataProvider
} from 'react-admin';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button as MuiButton,
    Box,
    Alert,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';

export const InvitationImportButton = () => {
    const [open, setOpen] = useState(false);
    const [file, setFile] = useState<File | null>(null);
    const [company, setCompany] = useState<number | ''>('');
    const [roles, setRoles] = useState<number[]>([]);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState<any>(null);
    const [companies, setCompanies] = useState<any[]>([]);
    const [rolesList, setRolesList] = useState<any[]>([]);

    const notify = useNotify();
    const refresh = useRefresh();
    const dataProvider = useDataProvider();
    const { identity } = useGetIdentity();
    const { permissions } = usePermissions();

    const isCompanyAdmin = permissions?.includes('ROLE_COMPANY_ADMIN') &&
                          !permissions?.includes('ROLE_OPERATOR') &&
                          !permissions?.includes('ROLE_PLATFORM_ADMIN');

    useEffect(() => {
        if (open) {
            if (!isCompanyAdmin) {
                dataProvider.getList('companies', {
                    pagination: { page: 1, perPage: 100 },
                    sort: { field: 'id', order: 'ASC' },
                    filter: {}
                }).then(({ data }) => {
                    setCompanies(data);
                }).catch((error) => {
                    console.error('Failed to load companies', error);
                });
            }

            dataProvider.getList('roles', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'id', order: 'ASC' },
                filter: {}
            }).then(({ data }) => {
                setRolesList(data);
            }).catch((error) => {
                console.error('Failed to load roles', error);
            });
        }
    }, [open, isCompanyAdmin, dataProvider]);

    const handleOpen = () => {
        setOpen(true);
        setFile(null);
        setCompany('');
        setRoles([]);
        setResult(null);
    };

    const handleClose = () => {
        setOpen(false);
        setFile(null);
        setCompany('');
        setRoles([]);
        setResult(null);
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0];
        if (selectedFile) {
            if (!selectedFile.name.endsWith('.csv') && !selectedFile.type.includes('csv')) {
                notify('Bitte eine CSV-Datei auswählen', { type: 'error' });
                return;
            }
            setFile(selectedFile);
        }
    };

    const handleImport = async () => {
        if (!file) {
            notify('Bitte eine Datei auswählen', { type: 'error' });
            return;
        }

        if (!isCompanyAdmin && !company) {
            notify('Bitte ein Unternehmen auswählen', { type: 'error' });
            return;
        }

        setUploading(true);
        setResult(null);

        try {
            const formData = new FormData();
            formData.append('file', file);
            if (!isCompanyAdmin) {
                formData.append('company', company.toString());
            }
            if (roles.length > 0) {
                formData.append('roles', roles.join(','));
            }

            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_SSO_URL}/api/invitations/import`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                body: formData
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setResult(data);
                notify(data.message, { type: 'success' });
                refresh();
            } else {
                notify(data.error || 'Import fehlgeschlagen', { type: 'error' });
                setResult(data);
            }
        } catch (error) {
            notify('Einladungen konnten nicht importiert werden', { type: 'error' });
        } finally {
            setUploading(false);
        }
    };

    return (
        <>
            <Button
                label="CSV-Import"
                onClick={handleOpen}
                startIcon={<UploadFileIcon />}
            />
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>Einladungen aus CSV-Datei importieren</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        <Alert severity="info">
                            CSV-Datei mit einer E-Mail-Adresse pro Zeile hochladen.
                            {isCompanyAdmin ?
                                ' Die Einladungen werden für Ihr Unternehmen angelegt.' :
                                ' Bitte das Unternehmen für diese Einladungen auswählen.'}
                        </Alert>

                        {!isCompanyAdmin && (
                            <FormControl fullWidth required>
                                <InputLabel>Unternehmen</InputLabel>
                                <Select
                                    value={company}
                                    onChange={(e) => setCompany(e.target.value as number)}
                                    label="Unternehmen"
                                >
                                    {companies.map((c: any) => (
                                        <MenuItem key={c.id} value={c.id}>
                                            {c.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        <FormControl fullWidth>
                            <InputLabel>Rollen (optional)</InputLabel>
                            <Select
                                multiple
                                value={roles}
                                onChange={(e) => setRoles(e.target.value as number[])}
                                label="Rollen (optional)"
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => {
                                            const role = rolesList.find((r: any) => r.id === value);
                                            return <Chip key={value} label={role?.name || value} size="small" />;
                                        })}
                                    </Box>
                                )}
                            >
                                {rolesList.map((role: any) => (
                                    <MenuItem key={role.id} value={role.id}>
                                        {role.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <MuiButton
                            variant="outlined"
                            component="label"
                            fullWidth
                        >
                            {file ? file.name : 'CSV-Datei auswählen'}
                            <input
                                type="file"
                                hidden
                                accept=".csv,text/csv"
                                onChange={handleFileChange}
                            />
                        </MuiButton>

                        {result && (
                            <Alert severity={result.errors?.length > 0 ? 'warning' : 'success'}>
                                <div>Importiert: {result.imported}</div>
                                {result.skipped > 0 && <div>Übersprungen: {result.skipped}</div>}
                                {result.errors?.length > 0 && (
                                    <div>
                                        <strong>Fehler:</strong>
                                        <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                                            {result.errors.map((error: string, idx: number) => (
                                                <li key={idx}>{error}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </Alert>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <MuiButton onClick={handleClose}>
                        {result ? 'Schließen' : 'Abbrechen'}
                    </MuiButton>
                    {!result && (
                        <MuiButton
                            onClick={handleImport}
                            variant="contained"
                            disabled={!file || uploading || (!isCompanyAdmin && !company)}
                        >
                            {uploading ? <CircularProgress size={24} /> : 'Importieren'}
                        </MuiButton>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
};
